import {useSignal} from '@preact/signals-react'
import {useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import moment from 'moment'
import {Button} from 'primereact/button'
import {InputText} from 'primereact/inputtext'
import {Tag} from 'primereact/tag'
import {ToggleButton} from 'primereact/togglebutton'
import {UniqueComponentId} from 'primereact/utils'
import React, {useEffect, useMemo, useRef} from 'react'
import PhoneInput from 'react-phone-input-2'
import {ReactComponent as NotifyImg} from '../../assets/images/undraw_new_notifications.svg'
import {SystemRole} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import {useUserHospital} from '../../context/hospital/UserHospitalContext'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {useAuth} from '../authentication/AuthProvider'
import {getClientId} from '../authentication/AuthUtils'
import {component} from '../form/Constant'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {isArrayAndNotEmpty} from '../observations/Utils'
import StyledHospitalOptions from '../styled-component/styled-options/StyledHospitalOptions'
import {convertKeyToLabel} from '../table-utils/TableUtils'
import {
    getSystemConfigurationEventKey,
    onSystemConfigurationChangeRemoveListener,
    systemConfigurationEvent,
    systemConfigurationTypes,
} from './broadcast/GenericLogEvent'
import {timeOptionKeys} from './Constants'
import {
    useAdditionalNotifyMethod,
    usePageAdditionalNotifyMethod,
} from './query-keys/AdditionNotifyMethodQueryManager'
import StyledNotifyMethodsForm from './StyledNotifyMethodsForm'
const InitialPageable = {
    pageRows: 9,
    pageNumber: 0,
    sortField: 'id',
    sortOrder: -1,
}
const InitialFilterRequest = {}
const StyledAdditionalNotifyMethod = () => {
    useSignals()
    const {user} = useAuth()
    const {currentHospital} = useUserHospital()
    const {timezoneId, userRole} = useAppConfig()
    const notifyMethodsFormRef = useRef(null)
    const filterRequest = useMemo(() => ({...InitialFilterRequest}), [])

    const queryClient = useQueryClient()
    const {mutateAsync: createOrUpdateAdditionalNotifyMethod} =
        useAdditionalNotifyMethod(QueryCRUDTypes.update, user?.id)

    const pageable = useSignal({...InitialPageable})
    const filterData = useSignal({
        ...filterRequest,
        hospitalId: userRole === SystemRole.ADMIN ? null : currentHospital.id,
    })

    const hospitalId = useMemo(() => {
        return userRole === SystemRole.ADMIN
            ? filterData.value.hospitalId
            : currentHospital.id
    }, [currentHospital.id, filterData.value.hospitalId, userRole])
    const {data, isFetching} = usePageAdditionalNotifyMethod(
        user?.id,
        hospitalId,
        pageable.value.pageNumber,
        pageable.value.pageRows,
        pageable.value.sortField,
        pageable.value.sortOrder,
        {
            ...filterData.value,
            hospitalId: hospitalId,
            // clientId : user?.clientId
        },
        !!user?.id && !!currentHospital?.id,
    )
    const dataList = useMemo(() => {
        let holderData = isArrayAndNotEmpty(data?.content)
            ? [...data?.content]
            : []
        return holderData
    }, [data, isFetching])

    const totalElements = useMemo(() => {
        return data?.totalElements || 0
    }, [data?.totalElements])

    //#region Event Listener on State Changes
    useEffect(() => {
        systemConfigurationEvent.on(
            `${getSystemConfigurationEventKey(
                systemConfigurationTypes.ADDITIONAL_METHOD_CHANGE,
            )}`,
            newData => {
                if (!newData.clientId || newData.clientId !== getClientId()) {
                    return
                }
                if (
                    !newData.hospitalId ||
                    newData.hospitalId !== currentHospital?.id
                ) {
                    return
                }
                if (!newData?.userId || newData?.userId === user?.id) {
                    return
                }
                if (!newData || !newData?.id) {
                    return
                }

                const {additionalNotifyMethodKeys} = QueryKeyManager(
                    QueryKeyTypes.additionalNotifyMethod,
                    user?.id,
                )
                queryClient.invalidateQueries({
                    queryKey: additionalNotifyMethodKeys.pattern(),
                })
                showNotification(
                    severityNotification.success,
                    'The Additional Notify Method has just been updated.',
                )

                if (notifyMethodsFormRef?.current) {
                    notifyMethodsFormRef.current.hideForm()
                }
            },
        )

        return () => {
            onSystemConfigurationChangeRemoveListener(
                systemConfigurationTypes.ADDITIONAL_METHOD_CHANGE,
            )
        }
    }, [])
    //#endregion

    const sortByDay = days => {
        if (!isArrayAndNotEmpty(days)) {
            return []
        }
        const weekDays = [
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
            'sunday',
        ]
        const sortedDays = days.sort((a, b) => {
            return weekDays.indexOf(a) - weekDays.indexOf(b)
        })
        const today = moment().tz(timezoneId).format('dddd').toLowerCase()

        return sortedDays.map(item => {
            return {day: item, today: item === today}
        })
    }
    const handleOnToggle = async (item, value) => {
        const toPostData = {
            ...item,
            hospitalId: currentHospital?.id,
            active: value,
        }
        const res = await createOrUpdateAdditionalNotifyMethod(toPostData)
        if (res?.id) {
            const {additionalNotifyMethodKeys} = QueryKeyManager(
                QueryKeyTypes.additionalNotifyMethod,
                user?.id,
            )
            queryClient.invalidateQueries({
                queryKey: additionalNotifyMethodKeys.pattern(),
            })
        }
    }
    const handleLoadMore = () => {
        let pageRows = pageable.value.pageRows
        if (pageRows < totalElements) {
            pageable.value = {
                ...pageable.value,
                pageRows: pageRows + 9,
            }
        }
    }
    return (
        <div>
            <div className="flex justify-content-between align-items-center">
                <div className="">
                    <h5 className="pt-2 mb-0 font-bold">
                        Additional Notify Methods
                    </h5>
                    <p>
                        Add additional methods to send notifications through
                        various channels such as phone number, SMS, etc{' '}
                    </p>
                </div>
                <div>
                    {filterData.value?.hospitalId && (
                        <Button
                            className="h-3rem col-12 px-3"
                            icon="pi pi-plus"
                            label="ADD NEW"
                            onClick={() => {
                                notifyMethodsFormRef.current.showForm()
                            }}
                        />
                    )}
                </div>
            </div>
            {userRole === SystemRole.ADMIN && (
                <div className="py-2">
                    <StyledHospitalOptions
                        optionType={component.dropdown}
                        onChange={hospitalId =>
                            (filterData.value = {
                                ...filterData.value,
                                hospitalId: hospitalId,
                            })
                        }
                    />
                </div>
            )}

            {dataList && isArrayAndNotEmpty(dataList) ? (
                <div className="grid my-2 ">
                    {dataList?.map((item, idx) => (
                        <div
                            key={`${UniqueComponentId()}`}
                            className=" col-12 md:col-6 lg:col-4 p-1 relative">
                            <div
                                className={`flex flex-column justify-content-between border-1 p-3 ${
                                    item.activeAndValid
                                        ? 'border-teal-500'
                                        : 'opacity-70 border-gray-300 hover:border-gray-400'
                                }  border-round-xl  h-full`}>
                                <div className="absolute">
                                    <Tag
                                        className="px-3"
                                        severity={
                                            !item.active
                                                ? 'danger'
                                                : item.activeAndValid
                                                ? 'success'
                                                : 'warning'
                                        }
                                        style={{
                                            borderTopRightRadius: 0,
                                            borderBottomLeftRadius: 0,
                                        }}
                                        value={item.status}
                                    />
                                </div>
                                <div className="p-2">
                                    {' '}
                                    <div className="flex justify-content-between mb-3">
                                        <div className="pt-4">
                                            <div className="flex gap-2">
                                                {' '}
                                                <h3 className="mb-1 uppercase text-md font-bold">
                                                    {idx + 1}. {item.name}
                                                </h3>
                                            </div>

                                            <p> - {item.description}</p>
                                        </div>
                                        <div className="">
                                            <ToggleButton
                                                className=" mr-2"
                                                onLabel="On"
                                                offLabel="Off"
                                                onIcon="pi pi-power-off"
                                                offIcon="pi pi-caret-right"
                                                checked={item.active}
                                                onChange={e =>
                                                    handleOnToggle(
                                                        item,
                                                        e.value,
                                                    )
                                                }
                                            />
                                            <Button
                                                className="h-3rem col-12 m-0 bg-teal-50 border-teal-50 text-teal-600 font-bold hover:text-teal-700"
                                                icon="pi pi-cog"
                                                onClick={() => {
                                                    notifyMethodsFormRef.current.showForm(
                                                        item,
                                                    )
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex align-items-center gap-2 mb-2">
                                        <div>
                                            <h6 className="mb-0">
                                                <span>
                                                    <i className="pi pi-clock mr-2"></i>
                                                </span>
                                                Time slot for receiving
                                                notifications:
                                            </h6>
                                        </div>
                                        <div className="font-bold text-lg">
                                            {item.time ===
                                            timeOptionKeys.customTime ? (
                                                <React.Fragment>
                                                    <label>
                                                        {item.startTime}
                                                    </label>
                                                    <div>
                                                        <i className="pi pi-chevron-right text-sm" />{' '}
                                                    </div>
                                                    <label>
                                                        {item.endTime}
                                                    </label>
                                                </React.Fragment>
                                            ) : (
                                                <label>
                                                    {convertKeyToLabel(
                                                        timeOptionKeys.allDay,
                                                    )}
                                                </label>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <h6 className="mb-0">
                                            <span>
                                                <i className="pi pi-calendar mr-2"></i>
                                            </span>
                                            Date for receiving notifications:{' '}
                                            <span className="font-bold text-lg">
                                                {item.days &&
                                                item.days.length > 0
                                                    ? `${item.days.length} days selected`
                                                    : 'No specific date'}
                                            </span>
                                        </h6>
                                    </div>
                                    <div className="grid gap-1 mb-3">
                                        {isArrayAndNotEmpty(item.days) &&
                                            sortByDay(item.days).map(
                                                (dayItem, dIdx) => (
                                                    <div
                                                        className="col p-0"
                                                        key={`${UniqueComponentId()}`}>
                                                        {' '}
                                                        <Button
                                                            className={`w-full  ${
                                                                dayItem.day ===
                                                                item.today
                                                                    ? 'border-gray-100 text-darkcyan'
                                                                    : 'text-700 border-gray-100 bg-white'
                                                            }  opacity-90`}
                                                            icon={
                                                                dayItem.day ===
                                                                    item.today &&
                                                                'pi pi-calendar'
                                                            }
                                                            label={convertKeyToLabel(
                                                                dayItem.day.slice(
                                                                    0,
                                                                    3,
                                                                ),
                                                            )}
                                                        />
                                                    </div>
                                                ),
                                            )}
                                    </div>
                                    <div className="mb-3">
                                        <h6 className="mb-0">
                                            <span>
                                                <i className="pi pi-phone mr-2"></i>
                                            </span>
                                            Phone Numbers:{' '}
                                            <span className="font-bold text-lg">
                                                {`${
                                                    item.totalPhoneActive
                                                } active  / ${
                                                    item.phone.length || 0
                                                } in total`}
                                            </span>
                                        </h6>
                                    </div>
                                    <div className="overflow-x-auto flex gap-2 mb-3">
                                        {item.phone.map((phoneItem, pIdx) => (
                                            <div
                                                key={`${UniqueComponentId()}`}
                                                className="flex mb-1 relative">
                                                <PhoneInput
                                                    id={`phone-input-${idx}-${pIdx}`}
                                                    className={`p-0 font-semibold cursor-auto`}
                                                    inputClass={
                                                        'font-semibold '
                                                    }
                                                    country={'gb'}
                                                    preferredCountries={[
                                                        'gb',
                                                        'us',
                                                        'ca',
                                                        'vn',
                                                        'bg',
                                                    ]}
                                                    inputStyle={{
                                                        minHeight: '40px',
                                                    }}
                                                    disabled={true}
                                                    alwaysDefaultMask={true}
                                                    value={phoneItem.phone}
                                                />
                                                <Tag
                                                    className={`absolute right-0 ${
                                                        !phoneItem.active
                                                            ? 'bg-gray-500'
                                                            : ''
                                                    }`}
                                                    value={
                                                        phoneItem.active
                                                            ? 'Active'
                                                            : 'Inactive'
                                                    }
                                                    style={{
                                                        borderTopLeftRadius: 0,
                                                        borderBottomRightRadius: 0,
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mb-3">
                                        <h6 className="mb-0">
                                            <span>
                                                <i className="pi pi-envelope mr-2"></i>
                                            </span>
                                            Emails:{' '}
                                            <span className="font-bold text-lg">
                                                {`${
                                                    item.totalEmailActive
                                                } active  / ${
                                                    item.email.length || 0
                                                } in total`}
                                            </span>
                                        </h6>
                                    </div>
                                    <div className="overflow-x-auto flex gap-2">
                                        {item.email?.map((emailItem, eIdx) => (
                                            <div
                                                key={`${UniqueComponentId()}`}
                                                className="flex mb-1 relative">
                                                <InputText
                                                    key={`${UniqueComponentId()}`}
                                                    className="col h-3rem font-semibold opacity-100"
                                                    defaultValue={
                                                        emailItem.email
                                                    }
                                                    disabled
                                                />
                                                <Tag
                                                    className={`absolute right-0 ${
                                                        !emailItem.active
                                                            ? 'bg-gray-500'
                                                            : ''
                                                    }`}
                                                    value={
                                                        emailItem.active
                                                            ? 'Active'
                                                            : 'Inactive'
                                                    }
                                                    style={{
                                                        borderTopLeftRadius: 0,
                                                        borderBottomRightRadius: 0,
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>{' '}
                        </div>
                    ))}
                    {dataList?.length < totalElements && (
                        <div className="col-12 flex align-items-center justify-content-center mt-2">
                            {' '}
                            <Button
                                className="bg-white text-blue-500 border-1 border-blue-500"
                                icon={`pi pi-spin ${
                                    isFetching ? 'pi-spinner' : ''
                                }`}
                                label={`Show more (Showing ${
                                    dataList?.length || 0
                                } of ${totalElements || 0} total record(s))`}
                                onClick={() => handleLoadMore()}
                            />
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex justify-content-center py-6">
                    <div>
                        {' '}
                        <NotifyImg className="h-17rem" />
                        <h5 className="mb-1 text-center">
                            There is no notify method added.
                        </h5>
                    </div>
                </div>
            )}

            <StyledNotifyMethodsForm
                ref={notifyMethodsFormRef}
                hospitalId={hospitalId}
            />
        </div>
    )
}

export default StyledAdditionalNotifyMethod
